/**
 * 听写节奏设置表单（"每个词读几遍、每遍之间隔几秒、词与词之间再等几秒"）。
 *
 * ## 为什么这三个数值得单独一个组件
 *
 * 它们是**听写能不能用的关键**：读一遍孩子写不完、间隔太短来不及落笔、
 * 没有词间停顿就会"上一个词还没写完下一个已经开始"。而它们对家长必须
 * **说人话** —— 界面上不能出现 `gapSeconds` 这种字段名，也不能只说"读几遍"
 * 而让人猜"间隔到底算在哪儿"。
 *
 * 所以每个输入框下面都写着它在时间线上的位置，并且**实时显示一句话的效果描述**
 * 和"整轮大约多久"。家长改一个数就能立刻看到"这样一轮大约 7 分钟"。
 *
 * ## ⚠️ 为什么输入框用**本地草稿**而不是直接受控
 *
 * 直接受控（`value={tempo.repeatCount}` + 只接受合法值）会有一个真实的可用性 bug：
 * 想从 2 改成 1 时必须先删掉 2，而"空串"不是合法值 → 父组件的状态不变 →
 * **输入框立刻弹回 2**，家长根本改不了。所以这里：
 *   · 输入过程中允许任意文本（草稿）；
 *   · 合法值才立刻提交给父组件（于是预览时长实时更新）；
 *   · 非法值**留着让家长看见**，并在下面标红说明范围；
 *   · 失焦时若仍是非法值，**恢复到上一个合法值**（不留一个坏值在界面上）。
 *
 * ## 上下限来自 shared，不在这里另写
 *
 * `DICTATION_TEMPO_LIMITS` 同时被数据库 CHECK、API 的 zod 使用。这里引用它，
 * 于是"界面允许选 6 遍、后端只收到 5 遍"这种漂移不可能发生。
 */
import { useEffect, useState } from 'react';
import { Input } from '../ui/Input';
import {
  DICTATION_TEMPO_LIMITS,
  estimateDictationSeconds,
  formatApproxDuration,
  type DictationTempoConfig,
} from '@fht/shared';

type TempoField = keyof DictationTempoConfig;

export interface TempoFormProps {
  tempo: DictationTempoConfig;
  onChange: (patch: Partial<DictationTempoConfig>) => void;
  /** 词数（用来算"整轮大约多久"）。拿不到时传 `null` → 显示「—」。 */
  wordCount: number | null;
  disabled?: boolean;
}

/** 三个字段各自的说明（**人话**，不出现字段名）。 */
export const TEMPO_FIELD_HELP: Record<TempoField, string> = {
  repeatCount: '同一个词连读几遍。读 2 遍最常见：第一遍写、第二遍检查。',
  gapSeconds: '同一词两遍之间、以及每个词读完之后各停几秒 —— 这就是孩子写字的时间。',
  betweenWordsSeconds:
    '换下一个词之前**额外**再停几秒（叠加在上面的间隔之上）。嫌一轮太久就把它调小。',
};

/** 三个字段的界面文案。 */
const FIELD_LABEL: Record<TempoField, string> = {
  repeatCount: '每个词读几遍',
  gapSeconds: '每遍之间停几秒',
  betweenWordsSeconds: '换词前再停几秒',
};

/** `1~5` 这样的范围说明（越界时标红用）。 */
export function tempoRangeText(field: TempoField): string {
  const { min, max } = DICTATION_TEMPO_LIMITS[field];
  return `${min}~${max}`;
}

/**
 * 把节奏说成一句人话（家长一眼看懂"这样会是什么感觉"）。
 *
 * 例：`每个词读 2 遍，两遍之间停 5 秒，换下一个词前再停 12 秒，每个词之间约有 17 秒书写时间`。
 */
export function describeTempo(tempo: DictationTempoConfig): string {
  const parts = [
    `每个词读 ${tempo.repeatCount} 遍`,
    tempo.gapSeconds === 0 ? '两遍之间不停' : `两遍之间停 ${tempo.gapSeconds} 秒`,
    tempo.betweenWordsSeconds === 0
      ? '换词之间不停'
      : `换下一个词前再停 ${tempo.betweenWordsSeconds} 秒`,
  ];
  const perWord = tempo.gapSeconds + tempo.betweenWordsSeconds;
  if (perWord > 0) parts.push(`每个词之间约有 ${perWord} 秒书写时间`);
  else parts.push('几乎没有书写时间');
  return parts.join('，');
}

/**
 * 解析一个输入框里的值。
 *
 * 返回：
 *   · `{ kind: 'empty' }` —— 空串（家长正在删数字）
 *   · `{ kind: 'invalid', message }` —— 不是整数或越界
 *   · `{ kind: 'ok', value }` —— 合法
 */
export function parseTempoInput(
  raw: string,
  field: TempoField,
):
  | { kind: 'empty' }
  | { kind: 'invalid'; message: string }
  | { kind: 'ok'; value: number } {
  const text = raw.trim();
  const { min, max } = DICTATION_TEMPO_LIMITS[field];
  if (text === '') return { kind: 'empty' };
  const value = Number(text);
  if (!Number.isInteger(value)) return { kind: 'invalid', message: `请填一个整数（${min}~${max}）` };
  if (value < min || value > max) return { kind: 'invalid', message: `请填 ${min}~${max} 之间的整数` };
  return { kind: 'ok', value };
}

export function TempoForm({ tempo, onChange, wordCount, disabled = false }: TempoFormProps) {
  const estimate = wordCount === null ? null : estimateDictationSeconds(wordCount, tempo);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(['repeatCount', 'gapSeconds', 'betweenWordsSeconds'] as TempoField[]).map((field) => (
          <TempoFieldInput
            key={field}
            field={field}
            value={tempo[field]}
            disabled={disabled}
            onCommit={(value) => onChange({ [field]: value } as Partial<DictationTempoConfig>)}
          />
        ))}
      </div>

      <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
        {describeTempo(tempo)}
        <span className="mt-0.5 block text-xs text-slate-500">
          这一轮大约 {formatApproxDuration(estimate)}
          {/*
            ⚠️ "约"字不能省：录音比 TTS 慢或快，真实时长会被实际音频长度带偏。
               写一个确定的数字，家长会拿它当承诺。
          */}
          （按朗读与停顿估算，实际会因录音长短略有出入）
        </span>
      </p>
    </div>
  );
}

/** 单个数字输入（带本地草稿，见文件头"为什么用本地草稿"）。 */
function TempoFieldInput({
  field,
  value,
  disabled,
  onCommit,
}: {
  field: TempoField;
  value: number;
  disabled: boolean;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const [error, setError] = useState<string | null>(null);

  /**
   * 外部值变了（例如"用上次的节奏"预填）→ 草稿跟着走。
   * ⚠️ 但**只在草稿本身合法且与外部值不同**时才同步：否则家长正在输入
   *    "1"（中间态）时会被外部值覆盖回去。
   */
  useEffect(() => {
    setDraft((current) => {
      const parsed = parseTempoInput(current, field);
      if (parsed.kind === 'ok' && parsed.value === value) return current;
      if (parsed.kind === 'invalid') return current; // 家长正在改，别抢他的输入
      return String(value);
    });
  }, [value, field]);

  return (
    <Input
      label={FIELD_LABEL[field]}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={draft}
      disabled={disabled}
      hint={`${TEMPO_FIELD_HELP[field]}（${tempoRangeText(field)}）`}
      error={error}
      onChange={(event) => {
        const raw = event.target.value;
        setDraft(raw);
        const parsed = parseTempoInput(raw, field);
        if (parsed.kind === 'ok') {
          setError(null);
          onCommit(parsed.value);
        } else if (parsed.kind === 'invalid') {
          setError(parsed.message);
        } else {
          setError(null);
        }
      }}
      onBlur={() => {
        const parsed = parseTempoInput(draft, field);
        if (parsed.kind === 'ok') {
          setError(null);
          return;
        }
        // 空着或写着非法值就离开这一格 → 回到上一个合法值（不留坏值）
        setDraft(String(value));
        setError(null);
      }}
    />
  );
}
